import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
  ShoppingCart,
  Person,
  CalendarToday,
  FilterList,
  Visibility,
  Print,
  Download,
} from '@mui/icons-material';
import { ventaService } from '../../../services/ventas';

// Styled Components con diseño El Piñón
const DashboardContainer = styled(Box)({
  background: 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)',
  minHeight: '100vh',
  padding: '24px',
  '@media (max-width: 768px)': {
    padding: '16px',
  },
});

const HeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '32px',
  padding: '24px',
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  borderRadius: '20px',
  color: 'white',
  boxShadow: '0 8px 32px rgba(46, 90, 51, 0.3)',
  flexWrap: 'wrap',
  gap: '16px',
  '@media (max-width: 768px)': {
    padding: '16px',
    marginBottom: '24px',
  },
});

const StatsCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '2px solid #e8f5e8',
  boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(46, 90, 51, 0.2)',
    borderColor: '#2e5a33',
  },
});

const ActionButton = styled(Button)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 90, 51, 0.4)',
  },
});

// Interfaces
interface Venta {
  id: string;
  numero: string;
  fecha: string;
  cliente: string;
  vendedor: string;
  productos: ProductoVenta[];
  subtotal: number;
  descuento: number;
  total: number;
  metodoPago: string;
  estado: string;
  tipo: 'presencial' | 'online';
}

interface ProductoVenta {
  id: string;
  nombre: string;
  cantidad: number;
  precio: number;
  subtotal: number;
}

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <StatsCard>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" fontWeight={700} color={color} gutterBottom>
            {value}
          </Typography>
          <Typography variant="h6" color="#2e5a33" fontWeight={600}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar
          sx={{
            backgroundColor: `${color}15`,
            color: color,
            width: 64,
            height: 64,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </StatsCard>
);

const DashboardVentas: React.FC = () => {
  const [ventas, setVentas] = useState<Venta[]>([]);
  const [filteredVentas, setFilteredVentas] = useState<Venta[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedVenta, setSelectedVenta] = useState<Venta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar ventas desde API
  const cargarVentas = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ventaService.getAll();
      const ventasData = Array.isArray(response.data) 
        ? response.data 
        : response.data.results || [];
      
      // Mapear los datos del API al formato esperado
      const ventasMapeadas: Venta[] = ventasData.map((venta: any) => ({
        id: venta.id.toString(),
        numero: venta.numero_venta || `VTA-${venta.id}`,
        fecha: venta.fecha_venta || venta.created_at,
        cliente: venta.cliente_nombre || venta.cliente?.nombre || 'Cliente',
        vendedor: venta.vendedor_nombre || venta.usuario?.username || 'Sistema',
        productos: venta.detalles?.map((detalle: any) => ({
          id: detalle.id.toString(),
          nombre: detalle.producto_nombre || 'Producto',
          cantidad: detalle.cantidad,
          precio: parseFloat(detalle.precio_unitario),
          subtotal: parseFloat(detalle.subtotal),
        })) || [],
        subtotal: parseFloat(venta.subtotal || 0),
        descuento: parseFloat(venta.descuento || 0),
        total: parseFloat(venta.total),
        metodoPago: venta.metodo_pago || 'Efectivo',
        estado: venta.estado === 'completada' ? 'Completada' : venta.estado === 'pendiente' ? 'Pendiente' : 'Cancelada',
        tipo: venta.tipo || 'presencial',
      }));

      setVentas(ventasMapeadas);
      setFilteredVentas(ventasMapeadas);
    } catch (err: any) {
      console.error('Error al cargar ventas:', err);
      setError(err.message || 'Error al cargar las ventas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarVentas();
  }, []);

  // Search and filter
  useEffect(() => {
    let filtered = ventas.filter(venta =>
      venta.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venta.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venta.vendedor.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter by tab
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    switch (tabValue) {
      case 1: // Hoy
        filtered = filtered.filter(v => new Date(v.fecha).toDateString() === today.toDateString());
        break;
      case 2: // Esta semana
        filtered = filtered.filter(v => new Date(v.fecha) >= weekAgo);
        break;
      case 3: // Este mes
        filtered = filtered.filter(v => new Date(v.fecha) >= monthAgo);
        break;
      default: // Todas
        break;
    }

    setFilteredVentas(filtered);
  }, [searchTerm, ventas, tabValue]);

  const getEstadoColor = (estado: string) => {
    switch (estado.toLowerCase()) {
      case 'completada': return '#4caf50';
      case 'pendiente': return '#ff9800';
      case 'cancelada': return '#f44336';
      default: return '#757575';
    }
  };

  const getTipoIcon = (tipo: string) => {
    return tipo === 'online' ? <ShoppingCart /> : <Receipt />;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('es-CL'),
      time: date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  // Calcular estadísticas
  const stats = {
    totalVentas: filteredVentas.length,
    ventasHoy: ventas.filter(v => new Date(v.fecha).toDateString() === new Date().toDateString()).length,
    montoTotal: filteredVentas.reduce((sum, v) => sum + v.total, 0),
    montoPromedio: filteredVentas.length > 0 ? filteredVentas.reduce((sum, v) => sum + v.total, 0) / filteredVentas.length : 0,
  };

  return (
    <DashboardContainer>
      {/* Header */}
      <HeaderSection>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src="/assets/logos/logo-el-pinon-nuevo.png"
            sx={{ width: 60, height: 60, border: '3px solid rgba(255,255,255,0.3)' }}
          />
          <Box>
            <Typography variant="h4" fontWeight={700} fontFamily="'Playfair Display', serif">
              Reporte de Ventas - El Piñón
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Análisis y seguimiento de todas las ventas
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <ActionButton onClick={() => {}}>
            <Download sx={{ mr: 1 }} />
            Exportar
          </ActionButton>
        </Box>
      </HeaderSection>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            marginBottom: 3,
            borderRadius: '12px',
          }}
        >
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} sx={{ color: '#2e5a33' }} />
        </Box>
      ) : (
        <>
      {/* Stats Cards */}
      <Box display="flex" gap={3} marginBottom={4} flexWrap="wrap">
        <Box flex="1 1 250px">
          <StatCard
            title="Total Ventas"
            value={stats.totalVentas.toString()}
            icon={<Receipt />}
            color="#2e5a33"
            subtitle="En el período"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Ventas Hoy"
            value={stats.ventasHoy.toString()}
            icon={<CalendarToday />}
            color="#4caf50"
            subtitle="Ventas del día"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Monto Total"
            value={formatCurrency(stats.montoTotal)}
            icon={<AttachMoney />}
            color="#9c27b0"
            subtitle="Ingresos totales"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Venta Promedio"
            value={formatCurrency(stats.montoPromedio)}
            icon={<TrendingUp />}
            color="#2196f3"
            subtitle="Por transacción"
          />
        </Box>
      </Box>

      <Box display="flex" gap={3}>
        {/* Lista de Ventas */}
        <Box flex={selectedVenta ? '1 1 60%' : '1 1 100%'}>
          <Paper sx={{ p: 3, borderRadius: '16px', boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)' }}>
            {/* Tabs y Search */}
            <Box mb={3}>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                  },
                  '& .Mui-selected': {
                    color: '#2e5a33 !important',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#2e5a33',
                  },
                }}
              >
                <Tab label="Todas" />
                <Tab label="Hoy" />
                <Tab label="Esta Semana" />
                <Tab label="Este Mes" />
              </Tabs>
              
              <TextField
                fullWidth
                placeholder="Buscar ventas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mt: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Tabla de Ventas */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Venta</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e5a33' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVentas.map((venta) => {
                    const dateTime = formatDateTime(venta.fecha);
                    return (
                      <TableRow 
                        key={venta.id} 
                        hover 
                        onClick={() => setSelectedVenta(venta)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ backgroundColor: '#2e5a33', color: 'white' }}>
                              {getTipoIcon(venta.tipo)}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600}>{venta.numero}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {venta.tipo === 'online' ? 'Online' : 'Presencial'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography fontWeight={600}>{venta.cliente}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Vendedor: {venta.vendedor}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography>{dateTime.date}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {dateTime.time}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600} color="#2e5a33" variant="h6">
                            {formatCurrency(venta.total)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {venta.metodoPago}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={venta.estado}
                            size="small"
                            sx={{
                              backgroundColor: `${getEstadoColor(venta.estado)}15`,
                              color: getEstadoColor(venta.estado),
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              sx={{ color: '#2e5a33' }}
                              title="Ver detalles"
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: '#2196f3' }}
                              title="Imprimir"
                            >
                              <Print />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Detalle de Venta */}
        {selectedVenta && (
          <Box flex="1 1 40%">
            <Paper sx={{ p: 3, borderRadius: '16px', boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)' }}>
              <Box display="flex" alignItems="center" justifyContent="between" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ backgroundColor: '#2e5a33', color: 'white' }}>
                    <Receipt />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {selectedVenta.numero}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(selectedVenta.fecha).date} a las {formatDateTime(selectedVenta.fecha).time}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Información del Cliente */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight={600} color="#2e5a33" mb={1}>
                  Información del Cliente
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Person fontSize="small" color="action" />
                  <Typography>{selectedVenta.cliente}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Atendido por: {selectedVenta.vendedor}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Método de pago: {selectedVenta.metodoPago}
                </Typography>
              </Box>

              {/* Productos */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight={600} color="#2e5a33" mb={1}>
                  Productos
                </Typography>
                <List>
                  {selectedVenta.productos.map((producto, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: '#e8f5e8', color: '#2e5a33' }}>
                          <ShoppingCart />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={producto.nombre}
                        secondary={`${producto.cantidad} x ${formatCurrency(producto.precio)}`}
                      />
                      <Typography fontWeight={600} color="#2e5a33">
                        {formatCurrency(producto.subtotal)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Totales */}
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(selectedVenta.subtotal)}</Typography>
                </Box>
                {selectedVenta.descuento > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Descuento:</Typography>
                    <Typography color="error">-{formatCurrency(selectedVenta.descuento)}</Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" fontWeight={700}>Total:</Typography>
                  <Typography variant="h6" fontWeight={700} color="#2e5a33">
                    {formatCurrency(selectedVenta.total)}
                  </Typography>
                </Box>

                <Box display="flex" gap={1} mt={2}>
                  <ActionButton onClick={() => {}} size="small" sx={{ flex: 1 }}>
                    <Print sx={{ mr: 1 }} />
                    Imprimir
                  </ActionButton>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
      </>
      )}
    </DashboardContainer>
  );
};

export default DashboardVentas;